import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BarChart3, Users, ShoppingCart, FileText, Scale, Inbox, Package, Wrench, Tags, MessageSquareQuote, HelpCircle,
  Home, Phone, Menu as MenuIcon, Image, Megaphone, Settings, LogOut, ExternalLink, KeyRound,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAdminAuth } from "@/admin/AuthContext";
import { cn } from "@/lib/utils";

const GROUPS = [
  ["Overview", [["/admin", "Dashboard", BarChart3], ["/admin/leads", "Leads", Users], ["/admin/orders", "Orders", ShoppingCart]]],
  ["Website", [["/admin/pages", "Page Manager", FileText], ["/admin/legal", "Legal Pages", Scale], ["/admin/requests", "Requests", Inbox], ["/admin/menus", "Menus", MenuIcon], ["/admin/media", "Media Library", Image]]],
  ["Content", [["/admin/products", "Products", Package], ["/admin/services", "Services", Wrench], ["/admin/plans", "Pricing Plans", Tags], ["/admin/testimonials", "Testimonials", MessageSquareQuote], ["/admin/faqs", "FAQs", HelpCircle], ["/admin/homepage", "Homepage", Home], ["/admin/contact-page", "Contact & Pricing Text", Phone]]],
  ["Settings", [["/admin/marketing", "Marketing & Tracking", Megaphone], ["/admin/settings", "General Settings", Settings], ["/admin/password", "Change Password", KeyRound]]],
];

const Side = () => {
  const { logout, user } = useAdminAuth();
  return (
    <div className="indigo-gradient flex h-full flex-col text-white">
      <Link to="/admin" className="flex items-center gap-2.5 px-6 py-6" data-testid="admin-sidebar-logo">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/10 font-heading text-sm font-extrabold text-cta">OB</span>
        <span className="font-heading text-sm font-extrabold leading-tight">OWN BRAND<span className="block text-[10px] tracking-[0.25em] text-sky">ADMIN</span></span>
      </Link>
      <nav className="flex-1 space-y-6 overflow-y-auto px-3 pb-6">
        {GROUPS.map(([g, items]) => (
          <div key={g}>
            <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">{g}</p>
            {items.map(([to, label, Icon]) => (
              <NavLink key={to} to={to} end={to === "/admin"} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) => cn("relative mb-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors", isActive ? "bg-white/12 text-white before:absolute before:left-0 before:top-2 before:h-[calc(100%-16px)] before:w-1 before:rounded-full before:bg-sky" : "text-white/70 hover:bg-white/5 hover:text-white")}>
                {({ isActive }) => <><Icon className={cn("h-4 w-4", isActive && "text-sky")} />{label}</>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <p className="truncate px-2 text-xs text-white/60">{user?.email}</p>
        <div className="mt-2 flex gap-2">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-white/10 py-2 text-xs font-bold hover:bg-white/15" data-testid="admin-view-site-link"><ExternalLink className="h-3.5 w-3.5" />View site</a>
          <button onClick={logout} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-cta py-2 text-xs font-bold text-navy hover:bg-cta-hover" data-testid="admin-logout-btn"><LogOut className="h-3.5 w-3.5" />Logout</button>
        </div>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => { document.title = "Admin | Own Brand Technologies"; }, [pathname]);
  return (
    <div className="min-h-screen bg-page">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block"><Side /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-0 p-0"><SheetTitle className="sr-only">Admin menu</SheetTitle><Side /></SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-white/95 px-4 backdrop-blur lg:hidden">
          <button onClick={() => setOpen(true)} className="grid h-10 w-10 place-items-center rounded-xl border border-line" aria-label="Open admin menu" data-testid="admin-mobile-menu-btn"><MenuIcon className="h-5 w-5 text-navy" /></button>
          <span className="font-heading font-bold text-navy">Admin</span>
        </header>
        <main className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
